import { AxiosError } from "axios";

/**
 * Shared contract for the restore endpoints (`POST /assets/{id}/restore`,
 * `POST /flights/{id}/restore`) and the campaign deleted-items listing
 * (`GET /campaigns/{campaignId}/deleted-items`).
 *
 * The requests themselves live in the stores that already own the matching
 * delete actions (`useCampaignDetailStore`, `campaignStore`); only the response
 * shapes and the error classification live here, because both stores and the
 * "Recently deleted" panel need them.
 *
 * Two error codes matter to the UI:
 *
 *  - `NO_RESTORABLE_DELETION` (404) covers an unknown id, another agency's id,
 *    something that was never deleted, *and* something that has already been
 *    restored. A second restore of the same thing lands here rather than
 *    replaying a 200, so after a successful undo this is "already restored",
 *    not a failure, and must not raise a red error toast.
 *  - `RESTORE_BLOCKED_BY_ANCESTOR` (409) carries a human-readable `message`
 *    naming what has to be restored first. That message is shown verbatim.
 */

export const RESTORE_ERROR_NO_RESTORABLE_DELETION = "NO_RESTORABLE_DELETION";
export const RESTORE_ERROR_BLOCKED_BY_ANCESTOR = "RESTORE_BLOCKED_BY_ANCESTOR";

/** `data` of a successful restore. */
export interface RestoreResultData {
  event_id: string;
  restored_at: string;
  retention_deadline: string | null;
  restored_records: Record<string, number>;
  restored_record_count: number;
  cancelled_reminder_ids: string[];
  cancelled_obligation_keys: string[];
  active_reference_keys: string[];
  replayed: boolean;
}

export interface RestoreResponse {
  success: boolean;
  message: string;
  data: RestoreResultData;
}

export type DeletedItemRootType = "flight" | "asset" | "asset_version";

/** One row of the campaign's deleted-items listing, newest deletion first. */
export interface DeletedItem {
  event_id: string;
  root_type: DeletedItemRootType;
  root_id: string;
  root_label: string | null;
  deleted_at: string | null;
  retention_deadline: string | null;
  remaining_days: number | null;
  scope_item_count: number | null;
  restorable: boolean;
  blocked_reason: string | null;
}

export interface DeletedItemsResponse {
  success: boolean;
  message: string;
  data: DeletedItem[];
}

/**
 * An `Error` (so every existing `catch (e) { e.message }` caller keeps working)
 * that also carries the server's `error_code` for the two cases the UI treats
 * differently from a generic failure.
 */
export class RestoreError extends Error {
  constructor(
    message: string,
    public readonly errorCode: string | null,
  ) {
    super(message);
    this.name = "RestoreError";
  }
}

/** Build a `RestoreError` from a failed restore request. */
export function toRestoreError(
  error: unknown,
  fallbackMessage: string,
): RestoreError {
  const axiosError = error as AxiosError<any>;
  const body = axiosError?.response?.data;
  const message = body?.message || body?.error || fallbackMessage;

  return new RestoreError(message, body?.error_code ?? null);
}

/**
 * True when the failure means "there is nothing left to restore" — including
 * the already-restored case. Callers treat this as a no-op, not an error.
 */
export function isAlreadyRestored(error: unknown): boolean {
  return (
    error instanceof RestoreError &&
    error.errorCode === RESTORE_ERROR_NO_RESTORABLE_DELETION
  );
}
